// src/pages/ListGoodDeedsPage.jsx

import React, { useState, Fragment, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import API from '../../services/API';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import Select from 'react-select';

const ListGoodDeedsPage = () => {
    // State for custom good deeds
    const [goodDeeds, setGoodDeeds] = useState([]);
    const [isAddDeedModalOpen, setIsAddDeedModalOpen] = useState(false);
    const [deedName, setDeedName] = useState('');
    const [deedDescription, setDeedDescription] = useState('');
    const [isDeedActive, setIsDeedActive] = useState(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deedToDelete, setDeedToDelete] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deedToEdit, setDeedToEdit] = useState(null);
    // For edit form fields
    const [editDeedName, setEditDeedName] = useState('');
    const [editDeedDescription, setEditDeedDescription] = useState('');
    const [editIsActive, setEditIsActive] = useState(true);
    const [categories, setCategories] = useState([]);
    // Add near the top, with other useState hooks:
    const [deedCategoryId, setDeedCategoryId] = useState(null);
    const [editDeedCategoryId, setEditDeedCategoryId] = useState(null);

    const [deedLocationId, setDeedLocationId] = useState(null);
    const [editDeedLocationId, setEditDeedLocationId] = useState(null);
    const locationOptions = [
        { label: "DALAM KAWASAN SEKOLAH", value: "DALAM KAWASAN SEKOLAH" },
        { label: "LUAR KAWASAN SEKOLAH", value: "LUAR KAWASAN SEKOLAH" },
        { label: "DALAM KAWASAN SEKOLAH (ASRAMA)", value: "DALAM KAWASAN SEKOLAH (ASRAMA)" },
        { label: "LUAR KAWASAN SEKOLAH (ASRAMA)", value: "LUAR KAWASAN SEKOLAH (ASRAMA)" },
    ];

    // Fetch all good deeds for the school
    const fetchGoodDeeds = () => {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'token': localStorage.getItem('token'),
        };

        API.get('/goodDeeds/getSchoolGoodDeeds', { headers })
            .then((response) => {
                if (response.data.response_code === 1000) {
                    const deeds = response.data.data || [];

                    // ✅ Sort so active (true) comes first
                    const sorted = [...deeds].sort((a, b) => {
                        if (a.is_active === b.is_active) return 0;
                        return a.is_active ? -1 : 1; // active first
                    });
    
                    setGoodDeeds(sorted);
                } else {
                    setGoodDeeds([]);
                }
            })
            .catch(() => setGoodDeeds([]));
    };
    const fetchCategories = async () => {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'token': localStorage.getItem('token'),
        };
        try {
            const resp = await API.get('/goodDeeds/getDeedEnumList', { headers });
            // If wrapped in .data (as the API returns {data: [...]})
            setCategories(resp.data || []);
        } catch (e) {
            setCategories([]);
        }
    };

    useEffect(() => {
        fetchGoodDeeds();
        fetchCategories();
    }, []);

    // Good deeds table columns
    const columns = [
        {
            name: "Amalan Baik",
            cell: row => (
                <div style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    minWidth: 160,
                    maxWidth: 400,
                    fontWeight: 500,
                    fontSize: 14
                }}>
                    {row.deed_name}
                </div>
            ),
            grow: 2,
            center: false, // Left-align for natural reading, you can set to true if you prefer centered
        },
        {
            name: "Description",
            cell: row => (
                <div
                    style={{
                        whiteSpace: "pre-line",        // respects \n for newlines
                        wordBreak: "break-word",       // breaks long words if needed
                        minWidth: 180,
                        maxWidth: 380,                 // max width, adjust as needed
                        fontSize: 15,
                        lineHeight: 1.65,
                        textAlign: "left",
                        padding: "8px 14px",           // padding for readability
                        borderRadius: 8,               // slightly rounded corners
                        margin: "2px 0",
                        color: "#313750",
                    }}
                >
                    {row.deed_description}
                </div>
            ),
            center: false,
            grow: 3,
        },
        {
            name: "Amount",
            selector: row => row.amount,
            center: true,
            minWidth: "70px",
            maxWidth: "90px",
            style: { textAlign: "center" },
        },
        {
            name: "Category",
            cell: row => (
                <span>
                    {row.main_category}
                    {row.sub_category ? ` - ${row.sub_category}` : ""}
                </span>
            ),
            center: false, // or true if you want center
            minWidth: "120px",
            grow: 2,
        },

        {
            name: "Active",
            selector: row => row.is_active ? "Yes" : "No",
            center: true,
        },
        {
            name: "Action",
            center: true,
            minWidth: "250px",
            cell: row => (
                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        className="btn btn-warning btn-sm"
                        style={{ minWidth: 60 }}
                        onClick={() => {
                            setDeedToEdit(row);
                            setEditDeedName(row.deed_name);
                            setEditDeedDescription(row.deed_description);
                            setEditIsActive(row.is_active);
                            setEditDeedCategoryId(
                                row.deed_enum_id !== undefined && row.deed_enum_id !== null
                                    ? String(row.deed_enum_id)
                                    : null
                            );
                            setEditModalOpen(true);
                        }}


                    >
                        Edit
                    </button>
                    <button
                        className="btn btn-danger btn-sm"
                        style={{ minWidth: 60 }}
                        onClick={() => {
                            setDeedToDelete(row);
                            setDeleteModalOpen(true);
                        }}
                    >
                        Deactivate
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            button: true,
        }

    ];

    // Modal open/close
    const openAddDeedModal = () => setIsAddDeedModalOpen(true);
    const closeAddDeedModal = () => {
        setIsAddDeedModalOpen(false);
        setDeedName(''); setDeedDescription(''); setIsDeedActive(true);
    };

    // Handle Add
    const handleAddGoodDeed = async () => {
        if (!deedName) {
            toast.error("Amalan Baik Name are required.");
            return;
        }
        try {
            const headers = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'token': localStorage.getItem('token'),
            };
            const payload = {
                deed_name: deedName,
                deed_description: deedDescription,
                amount: 1,
                is_active: isDeedActive,
                moeis_deed_id: deedCategoryId,
                location: deedLocationId
            };
            const response = await API.post('/goodDeeds/writeSchoolGoodDeed', payload, { headers });
            if (response.data.response_code === 1000) {
                toast.success("Custom good deed added successfully.");
                closeAddDeedModal();
                fetchGoodDeeds();
            } else {
                toast.error(response.data.message);
            }
        } catch (e) {
            toast.error("Error adding amalan baik");
        }
    };
    const editDeed = async (deed) => {
        if (!deed) return;
        try {
            const headers = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'token': localStorage.getItem('token'),
            };
            const payload = {
                deed_id: deed.deed_id,
                deed_name: editDeedName,
                deed_description: editDeedDescription,
                amount: 1,
                is_active: editIsActive,
                moeis_deed_id: editDeedCategoryId,
                location: editDeedLocationId,
            };
            const resp = await API.patch('/goodDeeds/editDeed', payload, { headers });
            if (resp.data.response_code === 1000) {
                toast.success(resp.data.message || "Edited successfully.");
                fetchGoodDeeds();
            } else {
                toast.error(resp.data.message || "Edit failed.");
            }
        } catch (e) {
            toast.error("Error editing amalan baik.");
        } finally {
            setEditModalOpen(false);
            setDeedToEdit(null);
        }
    };
    const categoryOptions = categories.map(cat => ({
        label: cat.main_category + (cat.sub_category ? ` - ${cat.sub_category}` : ""),
        value: String(cat.id)
    }));

    return (
        <Fragment>
            <div className="container-fluid">
                <div className="row">
                    <div className="">
                        <div className="card rounded-25">
                            <div className="card-header rounded-25 text-red d-flex justify-content-between align-items-center">
                                <h5>Amalan Baik</h5>
                                <button className="btn btn-primary" onClick={openAddDeedModal}>
                                    Add Amalan Baik
                                </button>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <DataTable
                                        columns={columns}
                                        data={goodDeeds}
                                        striped
                                        center
                                        persistTableHead
                                        noHeader
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Custom Good Deed Modal */}
            <Modal isOpen={isAddDeedModalOpen} toggle={closeAddDeedModal}>
                <ModalHeader toggle={closeAddDeedModal}>
                    Add Amalan Baik
                </ModalHeader>
                <ModalBody>
                    <div className="form-group">
                        <label>Amalan Baik</label>
                        <input
                            type="text"
                            className="form-control"
                            value={deedName}
                            onChange={e => setDeedName(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            className="form-control"
                            value={deedDescription}
                            onChange={e => setDeedDescription(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Is Active?</label>
                        <Select
                            options={[
                                { label: "Active", value: true },
                                { label: "Inactive", value: false }
                            ]}
                            value={isDeedActive ? { label: "Active", value: true } : { label: "Inactive", value: false }}
                            onChange={opt => setIsDeedActive(opt.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Category</label>
                        <Select
                            options={categories.map(cat => ({
                                label: cat.main_category + (cat.sub_category ? ` - ${cat.sub_category}` : ""),
                                value: cat.id
                            }))}
                            value={categories
                                .map(cat => ({
                                    label: cat.main_category + (cat.sub_category ? ` - ${cat.sub_category}` : ""),
                                    value: cat.id
                                }))
                                .find(opt => opt.value === deedCategoryId) || null}
                            onChange={opt => setDeedCategoryId(opt ? opt.value : null)}
                            placeholder="Select category"
                            isClearable
                        />
                    </div>
                    <div className="form-group">
                        <label>Location</label>
                        <Select
                            options={locationOptions}
                            value={locationOptions.find(opt => opt.value === deedLocationId) || null}
                            onChange={opt => setDeedLocationId(opt ? opt.value : null)}
                            placeholder="Select location"
                            isClearable
                        />
                    </div>

                </ModalBody>
                <ModalFooter>
                    <Button color="btn btn-dark" onClick={handleAddGoodDeed}>
                        Add Amalan Baik
                    </Button>
                    <Button color="btn btn-primary" onClick={closeAddDeedModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
                <ModalHeader toggle={() => setDeleteModalOpen(false)}>
                    Confirm Deactivate
                </ModalHeader>
                <ModalBody>
                    Are you sure you want to deactivate amalan baik <b>{deedToDelete?.deed_name}</b>?
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={async () => {
                        if (!deedToDelete) return;
                        try {
                            const headers = {
                                'Content-Type': 'application/json',
                                'Accept': 'application/json',
                                'token': localStorage.getItem('token'),
                            };
                            const schoolId = localStorage.getItem('school_id'); // Or however you get the school_id
                            console.log(schoolId, deedToDelete)
                            const payload = {
                                school_id: schoolId,
                                deed_id: deedToDelete.deed_id,
                            };
                            const resp = await API.delete('/goodDeeds/deleteDeed', { data: payload, headers });
                            if (resp.data.response_code === 1000) {
                                toast.success(resp.data.message || "Deleted successfully.");
                                fetchGoodDeeds();
                            } else {
                                toast.error(resp.data.message || "Delete failed.");
                            }
                        } catch (e) {
                            toast.error("Error deleting good deed.");
                        } finally {
                            setDeleteModalOpen(false);
                            setDeedToDelete(null);
                        }
                    }}>
                        Deactivate
                    </Button>
                    <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)}>
                <ModalHeader toggle={() => setEditModalOpen(false)}>
                    Edit Amalan Baik
                </ModalHeader>
                <ModalBody>
                    <div className="form-group">
                        <label>Deed Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={editDeedName}
                            onChange={e => setEditDeedName(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            className="form-control"
                            value={editDeedDescription}
                            onChange={e => setEditDeedDescription(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Is Active?</label>
                        <Select
                            options={[
                                { label: "Active", value: true },
                                { label: "Inactive", value: false }
                            ]}
                            value={editIsActive ? { label: "Active", value: true } : { label: "Inactive", value: false }}
                            onChange={opt => setEditIsActive(opt.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Category</label>
                        <Select
                            options={categoryOptions}
                            value={categoryOptions.find(opt => opt.value === (editDeedCategoryId ?? "")) || null}
                            onChange={opt => setEditDeedCategoryId(opt ? opt.value : null)}
                            placeholder="Select category"
                            isClearable
                        />
                    </div>
                    <div className="form-group">
                        <label>Location</label>
                        <Select
                            options={locationOptions}
                            value={locationOptions.find(opt => opt.value === editDeedLocationId) || null}
                            onChange={opt => setEditDeedLocationId(opt ? opt.value : null)}
                            placeholder="Select location"
                            isClearable
                        />
                    </div>


                </ModalBody>
                <ModalFooter>
                    <Button color="btn btn-warning" onClick={() => editDeed(deedToEdit)}>
                        Save
                    </Button>
                    <Button color="secondary" onClick={() => setEditModalOpen(false)}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

        </Fragment>
    );
};

export default ListGoodDeedsPage;
